import * as React from "react"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { Button, Checkbox, Icon, Skeleton, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import {
  scriptsInfiniteQueryOptions,
  scriptsFacetsQueryOptions,
  sentScriptsQueryOptions,
  useToggleScriptFavorite,
  useReorderScriptScenes,
  useAiEditScripts,
  useRevertScript,
} from "@/lib/queries"
import type { ScriptDto, ScriptScene, ScriptsListParams, ScriptsFilters as BackendScriptsFilters } from "@/lib/queries"
import type { SentScriptMeta, SentScriptsMap } from "@/features/marketplace/types/marketplace.types"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback"
import { ScriptsToolbar, type ScriptsFilters, type FilterOption } from "./ScriptsToolbar"
import { HighlightText } from "../../HighlightText"
// import { DiversificationImpact } from "./DiversificationImpact"
import { toast } from "sonner"
import { AiScriptEditorModal } from "../AiScriptEditorModal/AiScriptEditorModal"
import { SuggestScriptModal } from "../SuggestScriptModal/SuggestScriptModal"
import { useSuggestScriptsJob } from "../../../hooks/useSuggestScriptsJob"
import { ScriptsBulkActionBar } from "../ScriptsBulkActionBar/ScriptsBulkActionBar.js"
import { KeyboardShortcutsDialog } from "../../KeyboardShortcutsDialog/KeyboardShortcutsDialog"
import { RevertScriptDialog } from "../RevertScriptDialog/RevertScriptDialog"
import { SendToVendorDialog } from "../SendToVendorDialog/SendToVendorDialog.js"
import {
  getScriptDisplayTitle,
  isUsableScriptText,
} from "@/features/opportunities/lib/script-display-title"
import { ScriptSentToVendorCard, getSentStatusKind, type SentStatusKind } from "../ScriptSentToVendorCard/ScriptSentToVendorCard.js"
import { ScriptStoryboard } from "../ScriptStoryboard/ScriptStoryboard.js"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { trackScriptAiEdited, trackScriptCopied } from "@/lib/analytics-events"
import { formatOpportunityEngineLabel } from "../../../lib/format-opportunity"

// ─── Helpers ─────────────────────────────────────────────────────────

function parseDuration(raw: string | null): string {
  if (!raw) return ""
  const match = raw.match(/\d+/)
  return match ? `${match[0]} sec` : raw
}

type ScriptSection = { key: string; body: string }

function parseScriptSections(raw: string): ScriptSection[] {
  const re = /\b([A-Z][A-Z\s]{1,30}):\s*/g
  const matches: { key: string; index: number; end: number }[] = []
  let m: RegExpExecArray | null
  while ((m = re.exec(raw)) !== null) {
    matches.push({ key: m[1].trim(), index: m.index, end: m.index + m[0].length })
  }
  if (matches.length === 0) return []
  return matches.map((match, i) => {
    const start = match.end
    const end = i + 1 < matches.length ? matches[i + 1].index : raw.length
    return { key: match.key, body: raw.slice(start, end).trim() }
  }).filter((s) => s.body)
}

// Re-orders parsed script sections to match the scene order after drag-and-drop.
// Matching is fuzzy (lowercase substring); falls back to original order if fewer
// than half the sections can be matched to a scene.
function sortSectionsBySceneOrder(sections: ScriptSection[], scenes: ScriptScene[]): ScriptSection[] {
  if (scenes.length === 0 || sections.length === 0) return sections
  const used = new Set<number>()
  const sorted: ScriptSection[] = []
  for (const scene of scenes) {
    const name = scene.scene_name.toLowerCase().replace(/[^a-z0-9\s]/g, " ")
    const nameFirst = name.trim().split(/\s+/)[0] ?? ""
    const idx = sections.findIndex((sec, i) => {
      if (used.has(i)) return false
      const key = sec.key.toLowerCase()
      return name.includes(key) || (nameFirst.length > 1 && key.includes(nameFirst))
    })
    if (idx >= 0) { sorted.push(sections[idx]); used.add(idx) }
  }
  sections.forEach((sec, i) => { if (!used.has(i)) sorted.push(sec) })
  return sorted.length >= Math.ceil(sections.length / 2) ? sorted : sections
}

export function formatScriptForCopy(script: ScriptDto, untitled: string): string {
  const lines: string[] = []

  lines.push(getScriptDisplayTitle(script, untitled))
  lines.push("")

  if (script.adDescription) {
    lines.push(script.adDescription)
    lines.push("")
  }

  // Derive sequence from scenes order so drag-and-drop reordering is reflected automatically.
  // Falls back to the static sequence string when scenes are absent.
  const sequence = script.scenes.length > 0
    ? script.scenes.map((s) => s.scene_name).join(" → ")
    : script.sequence
  if (sequence) lines.push(`Sequence: ${sequence}`)
  if (script.concept) lines.push(`Concept: ${script.concept}`)
  if (script.duration) lines.push(`Duration: ${script.duration}`)
  if (script.presenter) lines.push(`Presenter: ${script.presenter}`)
  if (script.background) lines.push(`Background: ${script.background}`)
  if (script.product) lines.push(`Product: ${script.product}`)
  if (script.opportunityTitle) lines.push(`From: ${script.opportunityTitle}`)
  if (sequence || script.concept || script.duration || script.presenter || script.background || script.product || script.opportunityTitle) lines.push("")

  // Scene breakdown table
  if (script.scenes.length > 0) {
    for (const sc of script.scenes) {
      lines.push(`Scene ${sc.scene_number} — ${sc.scene_name}`)
      lines.push(`  Seeing: ${sc.seeing}`)
      lines.push(`  Hearing: ${sc.hearing}`)
      if (sc.product) lines.push(`  Product: ${sc.product}`)
      if (sc.props) lines.push(`  Props: ${sc.props}`)
    }
    lines.push("")
  }

  if (script.script) {
    const sections = parseScriptSections(script.script)
    if (sections.length > 0) {
      const ordered = sortSectionsBySceneOrder(sections, script.scenes)
      lines.push(ordered.map((s) => `${s.key}:\n${s.body}`).join("\n\n"))
    } else {
      lines.push(script.script.replace(/\b([A-Z]{2,}(?:\s[A-Z]+)*):\s*/g, "\n$1:\n").trim())
    }
  }

  if (script.rationale) {
    if (lines.length > 0 && lines[lines.length - 1] !== "") lines.push("")
    lines.push("")
    lines.push("Why this works")
    lines.push(script.rationale)
  }

  return lines.join("\n").trim()
}

// ─── Scenes table ─────────────────────────────────────────────────────

function ScenesTable({ scenes, search, onReorder }: { scenes: ScriptScene[]; search: string; onReorder: (sceneIds: string[]) => void }) {
  const { t } = useTranslation()
  const dragIndex = React.useRef<number | null>(null)
  const [dropTarget, setDropTarget] = React.useState<number | null>(null)

  function handleDragStart(index: number) {
    dragIndex.current = index
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault()
    if (dragIndex.current !== null && dragIndex.current !== index) {
      setDropTarget(index)
    }
  }

  function handleDrop(index: number) {
    if (dragIndex.current === null || dragIndex.current === index) return
    const reordered = [...scenes]
    const [moved] = reordered.splice(dragIndex.current, 1)
    reordered.splice(index, 0, moved)
    onReorder(reordered.map((sc) => sc.id))
    dragIndex.current = null
    setDropTarget(null)
  }

  function handleDragEnd() {
    dragIndex.current = null
    setDropTarget(null)
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border/40 bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border/40 bg-muted/30">
            <th className="w-5 py-2.5 pl-2 pr-1" />
            <th className="w-8 py-2.5 pl-1 pr-2 text-left text-[11px] font-semibold tabular-nums uppercase tracking-wide text-muted-foreground">
              {t("opportunities.scripts.scene.number")}
            </th>
            <th className="w-36 py-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="clapperboard" size="xs" className="text-muted-foreground/70" />
                {t("opportunities.scripts.scene.scene")}
              </span>
            </th>
            <th className="py-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="eye" size="xs" className="text-muted-foreground/70" />
                {t("opportunities.scripts.scene.seeing")}
              </span>
            </th>
            <th className="py-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="volume-2" size="xs" className="text-muted-foreground/70" />
                {t("opportunities.scripts.scene.hearing")}
              </span>
            </th>
            <th className="py-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="package" size="xs" className="text-muted-foreground/70" />
                {t("opportunities.scripts.scene.product")}
              </span>
            </th>
            <th className="py-2.5 pr-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="wrench" size="xs" className="text-muted-foreground/70" />
                {t("opportunities.scripts.scene.props")}
              </span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/30">
          {scenes.map((scene, index) => {
            const isHook = scene.scene_name.toLowerCase() === "hook"
            const isDragging = dragIndex.current === index
            const isOver = dropTarget === index
            return (
            <tr
              key={scene.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={() => handleDrop(index)}
              onDragEnd={handleDragEnd}
              className={cn(
                "align-top transition-opacity",
                isHook && "bg-success-subtle/60",
                isDragging && "opacity-40",
                isOver && "border-t-2 border-primary",
              )}
            >
              <td className="py-3.5 pl-2 pr-1 align-top">
                <Icon
                  name="grip-vertical"
                  size="xs"
                  className="cursor-grab text-muted-foreground/30 active:cursor-grabbing"
                />
              </td>
              <td className="py-3.5 pl-1 pr-2 align-top tabular-nums text-muted-foreground">{scene.scene_number}</td>
              <td className="py-3.5 pr-3 align-top">
                <span className={cn(
                  "inline-block rounded border px-2 py-0.5 text-xs font-medium",
                  isHook
                    ? "border-success/30 bg-success-subtle text-success-subtle-foreground"
                    : "border-border/50 bg-muted/40 text-foreground",
                )}>
                  <HighlightText text={scene.scene_name} query={search} />
                </span>
              </td>
              <td className="py-3.5 pr-3 align-top leading-relaxed text-foreground"><HighlightText text={scene.seeing} query={search} /></td>
              <td className="py-3.5 pr-3 align-top leading-relaxed text-foreground"><HighlightText text={scene.hearing} query={search} /></td>
              <td className="py-3.5 pr-3 align-top leading-relaxed text-foreground">{scene.product ? <HighlightText text={scene.product} query={search} /> : "—"}</td>
              <td className="py-3.5 pr-3 align-top leading-relaxed text-foreground"><HighlightText text={scene.props} query={search} /></td>
            </tr>
          )})}
        </tbody>
      </table>
    </div>
  )
}

// ─── Sent badge tooltip ───────────────────────────────────────────────

/** Presentation config for the inline header badge, keyed by collapsed status. */
const SENT_BADGE_PRESENTATION: Record<
  SentStatusKind,
  { labelKey: string; tooltipKey: string; icon: "check" | "x"; className: string }
> = {
  sent: {
    labelKey: "opportunities.scripts.sentBadge",
    tooltipKey: "opportunities.scripts.sentBadgeTooltip",
    icon: "check",
    className: "border-success/30 bg-success-subtle text-success-subtle-foreground",
  },
  failed: {
    labelKey: "opportunities.scripts.sentBadgeFailed",
    tooltipKey: "opportunities.scripts.sentBadgeTooltipFailed",
    icon: "x",
    className: "border-destructive/30 bg-destructive/10 text-destructive",
  },
}

function SentBadgeTooltipContent({
  sentMeta,
  t,
}: {
  sentMeta: SentScriptMeta
  t: ReturnType<typeof useTranslation>["t"]
}) {
  const kind = getSentStatusKind(sentMeta.status)
  const presentation = SENT_BADGE_PRESENTATION[kind]
  return (
    <div className="space-y-0.5 text-xs">
      <div className="flex items-center gap-1.5 font-medium">
        <Icon name={kind === "failed" ? "x-circle" : "check-circle"} size="xs" className="shrink-0 text-current" />
        {t(presentation.tooltipKey, { vendorName: sentMeta.vendorName })}
      </div>
      {sentMeta.sentByUserName && (
        <div className="text-muted-foreground">
          {t("opportunities.scripts.sentBadgeTooltipBy", { name: sentMeta.sentByUserName })}
        </div>
      )}
      <div className="text-muted-foreground">
        {t("opportunities.scripts.sentBadgeTooltipOn", {
          date: new Date(sentMeta.sentAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }),
        })}
      </div>
    </div>
  )
}

// ─── Storyboard badge ─────────────────────────────────────────────────

/**
 * Quiet chip marking storyboard presence — "Sketch" / "Animatic" on a script
 * row, an icon plus count on a group header. Icons follow the storyboard
 * feature's own language: clapperboard for the sketch sheet, play for the
 * animatic.
 */
function StoryboardBadge({
  icon,
  label,
  tooltip,
}: {
  icon: "clapperboard" | "play"
  label: React.ReactNode
  tooltip: string
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0 items-center gap-1 rounded border border-border/60 bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
            <Icon name={icon} size="xs" className="text-current" />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// ─── Expanded script row ──────────────────────────────────────────────

function ScriptExpanded({
  ref,
  script,
  index,
  isSelected,
  isExpanded,
  isFavorite,
  isFocused,
  isProcessing,
  sentMeta,
  onToggleSelected,
  onToggleExpanded,
  onToggleFavorite,
  onRequestFocus,
  onAiEdit,
  onRevert,
  onSceneReorder,
  onSendToVendor,
  onOpenOrder,
  search,
  setId,
}: {
  ref?: React.Ref<HTMLDivElement>
  script: ScriptDto
  index: number
  isSelected: boolean
  isExpanded: boolean
  isFavorite: boolean
  isFocused: boolean
  isProcessing: boolean
  sentMeta?: SentScriptMeta | null
  onToggleSelected: () => void
  onToggleExpanded: () => void
  onToggleFavorite: () => void
  onRequestFocus: () => void
  onAiEdit: () => void
  onRevert: () => void
  onSceneReorder: (sceneIds: string[]) => void
  onSendToVendor?: () => void
  onOpenOrder?: (orderId: string) => void
  search: string
  setId: string
}) {
  const { t } = useTranslation()
  const [copied, setCopied] = React.useState(false)
  const [linkCopied, setLinkCopied] = React.useState(false)
  const activeTenantId = useAuth((s) => s.activeTenantId)
  const displayTitle = getScriptDisplayTitle(
    script,
    t("opportunities.scripts.untitledTitle", "Untitled script"),
  )
  const conceptSubtitle =
    isUsableScriptText(script.concept) && script.concept!.trim() !== displayTitle
      ? script.concept!.trim()
      : null

  // The share token is the script's own id — the public page is rendered live
  // from the DB. Open it in a new tab (synchronous, so no popup blocker) and
  // copy the link as a convenience.
  const handleShare = React.useCallback(() => {
    const url = `${window.location.origin}/s/${script.id}`
    window.open(url, "_blank", "noopener,noreferrer")
    // navigator.clipboard is undefined in non-secure contexts and writeText can
    // throw synchronously, so guard before calling and swallow rejections.
    const copied = navigator.clipboard?.writeText(url)
    if (!copied) {
      toast.error(t("opportunities.scripts.shareError"))
      return
    }
    void copied.then(() => {
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2200)
      toast.success(t("opportunities.scripts.shareToast"))
    }).catch(() => {
      toast.error(t("opportunities.scripts.shareError"))
    })
  }, [script.id, t])

  const handleCopy = React.useCallback(() => {
    void navigator.clipboard.writeText(
      formatScriptForCopy(script, t("opportunities.scripts.untitledTitle", "Untitled script")),
    ).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success(t("opportunities.scripts.copyToast"))

      trackScriptCopied({
        script_ids: [script.id],
        script_count: 1,
        copy_mode: 'single',
        copy_format: 'markdown',
        opportunity_set_id: setId,
      })
    })
  }, [script, t, setId])

  const duration = parseDuration(script.duration)

  // Inline tail kept structural-only (scenes, duration). Long descriptive values
  // like presenter/background/product live in the expanded body as labeled chips
  // so they don't truncate awkwardly in the collapsed row.
  const tailParts = [
    script.scenes.length > 0 ? t("opportunities.scripts.scenes", { count: script.scenes.length }) : null,
    duration || null,
  ].filter((v): v is string => Boolean(v))

  const specs: { label: string; value: string }[] = [
    script.presenter ? { label: t("opportunities.scripts.presenter"), value: script.presenter } : null,
    script.background ? { label: t("opportunities.scripts.background"), value: script.background } : null,
    script.product ? { label: t("opportunities.scripts.scene.product"), value: script.product } : null,
  ].filter((v): v is { label: string; value: string } => v !== null)

  return (
    <div
      ref={ref}
      data-script-id={script.id}
      onMouseDown={onRequestFocus}
      aria-current={isFocused ? "true" : undefined}
      className={cn(
        "pl-8 pr-5 py-3 rounded-lg transition-colors duration-150 relative",
        isFocused && "bg-foreground/[0.04]",
        isSelected && !isFocused && "bg-primary/5",
        isProcessing && "border border-emerald-400/60 animate-pulse pointer-events-none opacity-70",
      )}
    >
      {/* Hook-first row. Checkbox and star are sibling click targets so they
          don't bubble into the expand toggle. */}
      <div className="flex items-start gap-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleSelected}
          aria-label={t("opportunities.scripts.selectScript")}
          className="mt-0.5 shrink-0"
          onClick={(e) => e.stopPropagation()}
        />
        <button
          type="button"
          onClick={onToggleFavorite}
          aria-label={isFavorite ? t("opportunities.scripts.unfavorite") : t("opportunities.scripts.favorite")}
          aria-pressed={isFavorite}
          className="-mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors hover:bg-muted"
        >
          <Icon
            name="star"
            size="xs"
            className={isFavorite ? "fill-warning text-warning" : "text-muted-foreground/40"}
          />
        </button>
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={isExpanded}
          className="group flex min-w-0 flex-1 items-start gap-3 text-left"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <p className="text-[15px] font-medium leading-snug tracking-tight text-foreground">
                <HighlightText text={displayTitle} query={search} />
              </p>
              {hasSketchStoryboard(script) && (
                <StoryboardBadge
                  icon="clapperboard"
                  label={t("opportunities.scripts.storyboardBadge.sketch", "Sketch")}
                  tooltip={t(
                    "opportunities.scripts.storyboardBadge.sketchTooltip",
                    "Has a sketch storyboard",
                  )}
                />
              )}
              {hasAnimaticStoryboard(script) && (
                <StoryboardBadge
                  icon="play"
                  label={t("opportunities.scripts.storyboardBadge.animatic", "Animatic")}
                  tooltip={t(
                    "opportunities.scripts.storyboardBadge.animaticTooltip",
                    "Has an animatic",
                  )}
                />
              )}
              {sentMeta && (() => {
                const badge = SENT_BADGE_PRESENTATION[getSentStatusKind(sentMeta.status)]
                return (
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className={cn(
                            "inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium",
                            badge.className,
                          )}
                        >
                          <Icon name={badge.icon} size="xs" className="text-current" />
                          {t(badge.labelKey)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[220px]">
                        <SentBadgeTooltipContent sentMeta={sentMeta} t={t} />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )
              })()}
            </div>
            {conceptSubtitle && !isExpanded && (
              <p
                className="mt-1 line-clamp-1 text-xs italic text-muted-foreground/90"
                title={conceptSubtitle}
              >
                <HighlightText text={conceptSubtitle} query={search} />
              </p>
            )}
            <p className="mt-1 truncate text-xs text-muted-foreground">
              <span className="font-medium tabular-nums text-foreground/80">S{index}</span>
              {tailParts.length > 0 && <> · {tailParts.join(" · ")}</>}
            </p>
          </div>
          <Icon
            name="chevron-right"
            size="xs"
            className={cn(
              "mt-1 shrink-0 text-muted-foreground/40 transition-transform duration-200 group-hover:text-muted-foreground",
              isExpanded && "rotate-90",
            )}
          />
        </button>
      </div>

      {/* Collapsible body — scenes table + actions */}
      <div
        className={cn(
          "grid transition-all duration-200 ease-in-out",
          isExpanded ? "mt-2 grid-rows-[1fr] border-t border-border/50 pt-3" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="pl-7">
            {(script.adDescription || script.concept) && (
              <div className="mb-4 flex flex-col gap-3">
                {script.adDescription && (
                  <section>
                    <p className="mb-1 font-mono text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                      {t("opportunities.scripts.adDescription")}
                    </p>
                    <p className="text-xs leading-relaxed text-foreground/85">
                      <HighlightText text={script.adDescription} query={search} />
                    </p>
                  </section>
                )}
                {script.concept && (
                  <section>
                    <p className="mb-1 font-mono text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">
                      {t("opportunities.scripts.concept")}
                    </p>
                    <p className="text-xs leading-relaxed text-foreground/85">
                      <HighlightText text={script.concept} query={search} />
                    </p>
                  </section>
                )}
              </div>
            )}

            {specs.length > 0 && (
              <dl className="mb-4 grid grid-cols-[5.5rem_1fr] gap-x-3 gap-y-1 border-t border-border/30 pt-3 text-[11px] text-muted-foreground/90">
                {specs.map((row) => (
                  <React.Fragment key={row.label}>
                    <dt className="font-medium text-muted-foreground/60">{row.label}</dt>
                    <dd className="leading-snug">
                      <HighlightText text={row.value} query={search} />
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
            <ScenesTable scenes={script.scenes} search={search} onReorder={onSceneReorder} />

            {/* Script metadata line */}
            <p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground/60">
              <Icon name="clock" size="xs" className="text-current" />
              <span>
                {t("opportunities.scripts.metaCreated", {
                  date: new Date(script.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }),
                })}
              </span>
              {script.isAiEdited && script.scenesCreatedAt && (
                <>
                  <span className="mx-0.5">·</span>
                  <Icon name="sparkles" size="xs" className="text-[var(--ai-text)]" />
                  <span>
                    {t("opportunities.scripts.metaEdited", {
                      date: new Date(script.scenesCreatedAt).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }),
                    })}
                  </span>
                </>
              )}
            </p>

            {script.rationale && (
              <div className="mt-4 rounded-lg border border-border/40 bg-muted/30 px-4 py-3">
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                  {t("opportunities.scripts.rationale")}
                </p>
                <p className="text-xs leading-relaxed text-foreground/90">
                  <HighlightText text={script.rationale} query={search} />
                </p>
              </div>
            )}

            {/* Storyboard — self-contained collapsible section owning its own
                generate/retry action (single source of truth). Requires a tenant. */}
            {activeTenantId && (
              <ScriptStoryboard script={script} setId={setId} tenantId={activeTenantId} />
            )}

            {/* Footer actions */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {/* Revert button */}
              {!isProcessing && script.canRevert && (
                <button
                  type="button"
                  onClick={onRevert}
                  title={script.previousVersionCreatedAt
                    ? t("opportunities.scripts.revert.tooltipDate", {
                        date: new Date(script.previousVersionCreatedAt).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }),
                      })
                    : undefined}
                  className="flex h-8 items-center gap-1.5 rounded-md border border-border/60 px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Icon name="refresh-cw" size="xs" className="text-current" />
                  {t("opportunities.scripts.revert.button")}
                </button>
              )}
              <button
                type="button"
                onClick={handleShare}
                aria-label={t("opportunities.scripts.share")}
                className={cn(
                  "flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition-colors",
                  linkCopied
                    ? "border-success/40 bg-success-subtle text-success-subtle-foreground"
                    : "border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Icon name={linkCopied ? "check" : "share"} size="xs" className="text-current" />
                {linkCopied ? t("opportunities.scripts.shareCopied") : t("opportunities.scripts.share")}
              </button>
              <button
                type="button"
                onClick={onAiEdit}
                disabled={isProcessing}
                className="flex h-8 items-center gap-1.5 rounded-sm bg-[var(--ai-fill-medium)] px-3 text-xs font-normal text-[var(--ai-text)] transition-colors hover:bg-[var(--ai-fill-chip)] disabled:pointer-events-none disabled:opacity-80"
              >
                <Icon name={isProcessing ? "loader" : "sparkles"} size="xs" className={cn("text-current", isProcessing && "animate-spin")} />
                {isProcessing ? t("opportunities.scripts.aiEditProcessing") : t("opportunities.scripts.aiEdit")}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className={cn(
                  "flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition-colors",
                  copied
                    ? "border-success/40 bg-success-subtle text-success-subtle-foreground"
                    : "border-border/60 text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <Icon name={copied ? "check" : "copy"} size="xs" className="text-current" />
                {copied ? t("opportunities.scripts.copied") : t("opportunities.scripts.copyScript")}
              </button>
              {onSendToVendor && !sentMeta && (
                <button
                  type="button"
                  onClick={onSendToVendor}
                  className="flex h-8 items-center gap-1.5 rounded-md border border-border/60 px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Icon name="upload" size="xs" className="text-current" />
                  {t("opportunities.scripts.sendToVendor")}
                </button>
              )}
            </div>

            {sentMeta && (
              <ScriptSentToVendorCard
                sentMeta={sentMeta}
                onOpenOrder={onOpenOrder ? () => onOpenOrder(sentMeta.orderId) : undefined}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Opportunity group row ────────────────────────────────────────────

function OpportunityScriptGroup({
  title,
  description,
  flowName,
  engine,
  scripts,
  selectedIds,
  openScriptIds,
  focusedScriptId,
  isOpen,
  onToggleOpen,
  onToggleSelected,
  onToggleGroupSelection,
  onToggleScriptOpen,
  onSetAllScriptsOpen,
  onToggleScriptFavorite,
  onRequestScriptFocus,
  onScriptAiEdit,
  onScriptRevert,
  onScriptSceneReorder,
  onScriptSendToVendor,
  onScriptOpenOrder,
  isScriptProcessing,
  getSentMeta,
  registerRowRef,
  search,
  setId,
}: {
  title: string
  description: string | null
  flowName: string | null
  engine: string | null
  scripts: ScriptDto[]
  selectedIds: Set<string>
  openScriptIds: Set<string>
  focusedScriptId: string | null
  isOpen: boolean
  onToggleOpen: () => void
  onToggleSelected: (id: string) => void
  onToggleGroupSelection: () => void
  onToggleScriptOpen: (id: string) => void
  onSetAllScriptsOpen: (scriptIds: string[], open: boolean) => void
  onToggleScriptFavorite: (script: ScriptDto) => void
  onRequestScriptFocus: (id: string) => void
  onScriptAiEdit: (id: string) => void
  onScriptRevert: (script: ScriptDto) => void
  onScriptSceneReorder: (scriptId: string, sceneIds: string[]) => void
  onScriptSendToVendor?: (scriptId: string) => void
  onScriptOpenOrder?: (orderId: string) => void
  isScriptProcessing: (scriptId: string) => boolean
  getSentMeta?: (scriptId: string) => SentScriptMeta | null
  registerRowRef: (id: string, el: HTMLDivElement | null) => void
  search: string
  setId: string
}) {
  const { t } = useTranslation()
  // Defer mounting heavy ScriptExpanded children until the group is first
  // opened. Once mounted we keep them alive so collapse/reopen is instant.
  const hasBeenOpened = React.useRef(isOpen)
  if (isOpen) hasBeenOpened.current = true
  const shouldRenderScripts = hasBeenOpened.current

  const groupSelectedCount = scripts.filter((s) => selectedIds.has(s.id)).length
  const allGroupSelected = scripts.length > 0 && groupSelectedCount === scripts.length
  const someGroupSelected = groupSelectedCount > 0 && !allGroupSelected
  const sketchCount = scripts.filter(hasSketchStoryboard).length
  const animaticCount = scripts.filter(hasAnimaticStoryboard).length
  const expandedScriptCount = scripts.reduce((n, s) => n + (openScriptIds.has(s.id) ? 1 : 0), 0)
  const allScriptsExpanded = scripts.length > 0 && expandedScriptCount === scripts.length

  return (
    <div className={cn(
      "rounded-md border bg-card transition-[border-color,box-shadow] duration-200",
      someGroupSelected || allGroupSelected
        ? "border-primary/40 shadow-[0_0_0_1px_hsl(var(--primary)/0.2)]"
        : "border-border/50",
    )}>
      {/* Header: per-group select + clickable title region. Checkbox is a
          sibling of the toggle button so it doesn't fire expand on click. */}
      <div className="flex items-center gap-3 px-5 py-4">
        <Checkbox
          checked={someGroupSelected ? "indeterminate" : allGroupSelected}
          onCheckedChange={onToggleGroupSelection}
          aria-label={t("opportunities.scripts.selectGroup", { title })}
          className="shrink-0"
        />
        <button
          type="button"
          onClick={onToggleOpen}
          aria-expanded={isOpen}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm font-semibold text-foreground">
              <HighlightText text={title} query={search} />
            </span>
            {description && (
              <p className="truncate text-xs text-muted-foreground/80">
                <HighlightText text={description} query={search} />
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {sketchCount > 0 && (
              <StoryboardBadge
                icon="clapperboard"
                label={sketchCount}
                tooltip={t("opportunities.scripts.storyboardBadge.groupSketch", {
                  count: sketchCount,
                })}
              />
            )}
            {animaticCount > 0 && (
              <StoryboardBadge
                icon="play"
                label={animaticCount}
                tooltip={t("opportunities.scripts.storyboardBadge.groupAnimatic", {
                  count: animaticCount,
                })}
              />
            )}
            {engine && (
              <span className="rounded border border-border/60 bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                <HighlightText text={formatOpportunityEngineLabel(engine, t)} query={search} />
              </span>
            )}
            {flowName && (
              <span className="rounded border border-border/60 bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                <HighlightText text={flowName} query={search} />
              </span>
            )}
            {groupSelectedCount > 0 && (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium tabular-nums text-primary">
                {t("opportunities.scripts.selectedCount", { count: groupSelectedCount })}
              </span>
            )}
            <span className="ml-1 text-xs tabular-nums text-muted-foreground">
              {t("opportunities.scripts.count", { count: scripts.length })}
            </span>
            <Icon
              name="chevron-right"
              size="xs"
              className={cn(
                "shrink-0 text-muted-foreground/50 transition-transform duration-200",
                isOpen && "rotate-90",
              )}
            />
          </div>
        </button>
      </div>

      {/* Expanded scripts */}
      <div
        className={cn(
          "grid transition-all duration-200 ease-in-out",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="border-t border-border/40 bg-muted/30 rounded-b-xl">
            {shouldRenderScripts && (
              <>
                {scripts.length > 1 && (
                  <div className="flex justify-end px-5 pt-2">
                    <button
                      type="button"
                      onClick={() =>
                        onSetAllScriptsOpen(
                          scripts.map((s) => s.id),
                          !allScriptsExpanded,
                        )
                      }
                      className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <Icon
                        name="chevron-right"
                        size="xs"
                        className={cn(
                          "transition-transform duration-150",
                          allScriptsExpanded ? "-rotate-90" : "rotate-90",
                        )}
                      />
                      {allScriptsExpanded
                        ? t("opportunities.scripts.collapseAll")
                        : t("opportunities.scripts.expandAll")}
                    </button>
                  </div>
                )}
                <div className="divide-y divide-border/40">
                {scripts.map((script, i) => (
                  <div
                    key={script.id}
                    className={cn(
                      "transition-all duration-200",
                      isOpen
                        ? "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:fill-mode-both"
                        : "opacity-0",
                    )}
                    style={{ animationDelay: isOpen ? `${Math.min(i * 30, 150)}ms` : "0ms" }}
                  >
                    <ScriptExpanded
                      ref={(el) => registerRowRef(script.id, el)}
                      script={script}
                      index={i + 1}
                      isSelected={selectedIds.has(script.id)}
                      isExpanded={openScriptIds.has(script.id)}
                      isFavorite={script.isFavorite}
                      isFocused={focusedScriptId === script.id}
                      isProcessing={isScriptProcessing(script.id)}
                      sentMeta={getSentMeta ? getSentMeta(script.id) : null}
                      onToggleSelected={() => onToggleSelected(script.id)}
                      onToggleExpanded={() => onToggleScriptOpen(script.id)}
                      onToggleFavorite={() => onToggleScriptFavorite(script)}
                      onRequestFocus={() => onRequestScriptFocus(script.id)}
                      onAiEdit={() => onScriptAiEdit(script.id)}
                      onRevert={() => onScriptRevert(script)}
                      onSceneReorder={(sceneIds) => onScriptSceneReorder(script.id, sceneIds)}
                      onSendToVendor={onScriptSendToVendor ? () => onScriptSendToVendor(script.id) : undefined}
                      onOpenOrder={onScriptOpenOrder}
                      search={search}
                      setId={setId}
                    />
                  </div>
                ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Filtering ────────────────────────────────────────────────────────

const PAGE_SIZE = 30
const SEARCH_DEBOUNCE_MS = 300
/** Auto-expand the first opportunity group and its script rows on tab entry. */
const EXPAND_FIRST_OPPORTUNITY_ON_ENTER = false

const DEFAULT_FILTERS: ScriptsFilters = {
  topic: "all",
  concept: "all",
  presenter: "all",
  background: "all",
  sequence: "all",
  favoritesOnly: "all",
  sentStatus: "all",
  hasSketch: "all",
  hasAnimatic: "all",
}

/**
 * Storyboard presence, as the list shows and filters it. The lifecycle status is
 * the source of truth: READY means a rendered sketch sheet / animatic frames
 * exist for the script, whatever the state of any later regeneration.
 */
function hasSketchStoryboard(script: ScriptDto): boolean {
  return script.storyboardStatus === "READY"
}

function hasAnimaticStoryboard(script: ScriptDto): boolean {
  return script.storyboardPanelStatus === "READY"
}

/** Map the toolbar's "all" sentinel + favorites toggle to the backend's filter shape. */
function deriveBackendFilters(filters: ScriptsFilters): BackendScriptsFilters {
  return {
    topic: filters.topic !== "all" ? filters.topic : undefined,
    concept: filters.concept !== "all" ? filters.concept : undefined,
    presenter: filters.presenter !== "all" ? filters.presenter : undefined,
    background: filters.background !== "all" ? filters.background : undefined,
    sequence: filters.sequence !== "all" ? filters.sequence : undefined,
    favoritesOnly: filters.favoritesOnly === "favorites" ? true : undefined,
  }
}

function toOptions(values: string[] | undefined): FilterOption[] {
  if (!values) return []
  const seen = new Set<string>()
  const opts: FilterOption[] = []
  for (const v of values) {
    if (v && !seen.has(v)) {
      seen.add(v)
      opts.push({ value: v, label: v })
    }
  }
  return opts
}

function hasActiveFilters(filters: ScriptsFilters, search: string): boolean {
  if (search.trim() !== "") return true
  return (
    filters.topic !== "all" ||
    filters.concept !== "all" ||
    filters.presenter !== "all" ||
    filters.background !== "all" ||
    filters.sequence !== "all" ||
    filters.favoritesOnly === "favorites" ||
    filters.sentStatus !== "all" ||
    filters.hasSketch !== "all" ||
    filters.hasAnimatic !== "all"
  )
}

// ─── Loading skeleton ─────────────────────────────────────────────────

function ScriptGroupSkeleton({ scriptsCount = 0 }: { scriptsCount?: number }) {
  return (
    <div className="rounded-md border border-border bg-card">
      <div className="flex items-center gap-3 px-5 py-4">
        <Skeleton className="h-4 w-4 shrink-0 rounded" />
        <Skeleton className="h-3.5 w-3.5 shrink-0 rounded" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton className="h-4 w-48 rounded" />
          <Skeleton className="h-3 w-72 rounded" />
        </div>
        <Skeleton className="h-3.5 w-14 rounded" />
      </div>
      {scriptsCount > 0 && (
        <div className="divide-y divide-border/40 border-t border-border/40">
          {Array.from({ length: scriptsCount }).map((_, j) => (
            <div key={j} className="px-5 py-3">
              <div className="flex items-start gap-3">
                <Skeleton className="mt-0.5 h-4 w-4 shrink-0 rounded" />
                <Skeleton className="mt-0.5 h-4 w-4 shrink-0 rounded-full" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-4 w-full max-w-lg rounded" />
                  <Skeleton className="h-3 w-48 rounded" />
                </div>
                <Skeleton className="mt-1 h-3 w-3 shrink-0 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────

export interface CompletedScriptsTabProps {
  setId: string
  /** Deep-link target — auto-expands the opportunity group + script row and scrolls into view. */
  highlightScriptId?: string
  expandFirstOpportunityOnEnter?: boolean
  /** When true, the scripts list polls every 3s to show newly created scripts in real time. */
  isGenerating?: boolean
  /** Task-card jump — drop search/filters so a hidden script can mount. */
  revealTaskTarget?: boolean
}

export function CompletedScriptsTab({
  setId,
  highlightScriptId,
  expandFirstOpportunityOnEnter = EXPAND_FIRST_OPPORTUNITY_ON_ENTER,
  isGenerating = false,
  revealTaskTarget = false,
}: CompletedScriptsTabProps) {
  const { t } = useTranslation()

  // ─── Filter UI state ─────────────────────────────────────────────
  const [inputSearch, setInputSearch] = React.useState("")
  const [debouncedSearch, setDebouncedSearch] = React.useState("")
  const [filters, setFilters] = React.useState<ScriptsFilters>(DEFAULT_FILTERS)

  // Debounce the search term that drives the server fetch so each keystroke
  // doesn't fire a request. The toolbar manages its own immediate-feedback
  // input — `inputSearch` is just our local mirror for the "Clear filters"
  // affordance and selected-count math.
  const { debounced: applyDebouncedSearch, cancel: cancelDebounce } = useDebouncedCallback(
    setDebouncedSearch,
    SEARCH_DEBOUNCE_MS,
  )
  const handleSearchChange = React.useCallback(
    (next: string) => {
      setInputSearch(next)
      applyDebouncedSearch(next)
    },
    [applyDebouncedSearch],
  )

  const shouldRevealTaskTarget = revealTaskTarget || Boolean(highlightScriptId)
  const listSearch = shouldRevealTaskTarget ? "" : debouncedSearch
  const listFilters = shouldRevealTaskTarget ? DEFAULT_FILTERS : filters

  React.useLayoutEffect(() => {
    if (!shouldRevealTaskTarget) return
    cancelDebounce()
    setInputSearch("")
    setDebouncedSearch("")
    setFilters(DEFAULT_FILTERS)
  }, [shouldRevealTaskTarget, cancelDebounce])

  // ─── Server data ────────────────────────────────────────────────
  const backendFilters = React.useMemo(() => deriveBackendFilters(listFilters), [listFilters])
  const queryParams = React.useMemo<ScriptsListParams>(
    () => ({ setId, search: listSearch, filters: backendFilters, pageSize: PAGE_SIZE }),
    [setId, listSearch, backendFilters],
  )

  const activeTenantId = useAuth((s) => s.activeTenantId)

  // ─── Suggest Script modal ────────────────────────────────────────
  const [suggestOpen, setSuggestOpen] = React.useState(false)
  const suggestJob = useSuggestScriptsJob({ tenantId: activeTenantId ?? "", setId })

  const listQuery = useInfiniteQuery({
    ...scriptsInfiniteQueryOptions(queryParams),
    refetchInterval: isGenerating ? 3_000 : false,
  })
  const facetsQuery = useQuery(scriptsFacetsQueryOptions(setId))
  const sentScriptsQuery = useQuery(sentScriptsQueryOptions(activeTenantId))
  const sentScriptsMap: SentScriptsMap = sentScriptsQuery.data ?? {}

  // Loaded items across every fetched page in render order. The server returns
  // scripts in opportunity-stable order, so grouping by `opportunityId` below
  // gives contiguous groups without sorting.
  const flattenedItems = React.useMemo(
    () => listQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [listQuery.data],
  )

  // Send status comes from the marketplace `sentScriptsMap` and storyboard
  // presence rides on the script rows themselves — neither is a backend list
  // filter, so both lenses are applied client-side over the loaded items,
  // unlike the server-driven filters.
  const isClientFiltered =
    listFilters.sentStatus !== "all" || listFilters.hasSketch === "yes" || listFilters.hasAnimatic === "yes"
  const visibleItems = React.useMemo(() => {
    if (!isClientFiltered) return flattenedItems
    const wantSent = listFilters.sentStatus === "sent"
    return flattenedItems.filter(
      (s) =>
        (listFilters.sentStatus === "all" || (sentScriptsMap[s.id] != null) === wantSent) &&
        (listFilters.hasSketch !== "yes" || hasSketchStoryboard(s)) &&
        (listFilters.hasAnimatic !== "yes" || hasAnimaticStoryboard(s)),
    )
  }, [
    flattenedItems,
    isClientFiltered,
    listFilters.sentStatus,
    listFilters.hasSketch,
    listFilters.hasAnimatic,
    sentScriptsMap,
  ])

  const totalCount = listQuery.data?.pages[0]?.totalCount ?? 0
  const filtersActive = hasActiveFilters(listFilters, listSearch)

  // ─── Filter option lists (from facets endpoint, set-scoped) ─────
  const topicOptions = React.useMemo(() => toOptions(facetsQuery.data?.topics), [facetsQuery.data])
  const conceptOptions = React.useMemo(() => toOptions(facetsQuery.data?.concepts), [facetsQuery.data])
  const presenterOptions = React.useMemo(() => toOptions(facetsQuery.data?.presenters), [facetsQuery.data])
  const backgroundOptions = React.useMemo(() => toOptions(facetsQuery.data?.backgrounds), [facetsQuery.data])
  const sequenceOptions = React.useMemo(() => toOptions(facetsQuery.data?.sequences), [facetsQuery.data])

  // ─── Selection ───────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())

  // ─── Group + script expansion state ─────────────────────────────
  const [openGroupIds, setOpenGroupIds] = React.useState<Set<number>>(new Set())
  const [openScriptIds, setOpenScriptIds] = React.useState<Set<string>>(new Set())

  // Expand first opportunity group + all its scripts once the first page lands.
  const hasInitialized = React.useRef(false)
  const appliedDeepLinkRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!highlightScriptId) {
      appliedDeepLinkRef.current = null
      return
    }
    if (appliedDeepLinkRef.current === highlightScriptId) return

    const script = flattenedItems.find((s) => s.id === highlightScriptId)
    if (!script) {
      if (listQuery.hasNextPage && !listQuery.isFetchingNextPage) {
        void listQuery.fetchNextPage()
      }
      return
    }

    appliedDeepLinkRef.current = highlightScriptId
    setOpenGroupIds((prev) => {
      const next = new Set(prev)
      next.add(script.opportunityId)
      return next
    })
    setOpenScriptIds((prev) => {
      const next = new Set(prev)
      next.add(script.id)
      return next
    })
    setFocusedScriptId(script.id)
  }, [
    highlightScriptId,
    flattenedItems,
    listQuery.hasNextPage,
    listQuery.isFetchingNextPage,
    listQuery.fetchNextPage,
  ])

  React.useEffect(() => {
    if (!expandFirstOpportunityOnEnter) return
    if (hasInitialized.current || flattenedItems.length === 0) return
    hasInitialized.current = true
    const firstOppId = flattenedItems[0]?.opportunityId
    if (firstOppId == null) return
    const firstGroupScripts = flattenedItems.filter((s) => s.opportunityId === firstOppId)
    setOpenGroupIds(new Set([firstOppId]))
    setOpenScriptIds(new Set(firstGroupScripts.map((s) => s.id)))
  }, [expandFirstOpportunityOnEnter, flattenedItems])

  // ─── Keyboard focus state — independent of DOM focus so Tab still works normally
  const [focusedScriptId, setFocusedScriptId] = React.useState<string | null>(null)
  const rowRefs = React.useRef(new Map<string, HTMLDivElement>())

  // Single source of truth for favorite state lives in the React Query cache.
  // The mutation does the optimistic flip there so all readers (rows, filter,
  // keyboard `f`) see the same value without a parallel override map.
  const toggleFavoriteMutation = useToggleScriptFavorite(setId)
  const reorderScenesMutation = useReorderScriptScenes(setId)

  const handleSceneReorder = React.useCallback(
    (scriptId: string, sceneIds: string[]) => {
      reorderScenesMutation.mutate({ scriptId, sceneIds })
    },
    [reorderScenesMutation],
  )

  // ─── Shortcut help dialog (Linear-style — `?` toggles it open) ─────
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false)
  const scriptShortcuts = React.useMemo(
    () => [
      { keys: ["j", "↓"], label: t("opportunities.scripts.toolbar.shortcuts.navigateDown") },
      { keys: ["k", "↑"], label: t("opportunities.scripts.toolbar.shortcuts.navigateUp") },
      { keys: ["Enter"], label: t("opportunities.scripts.toolbar.shortcuts.expand") },
      { keys: ["Space"], label: t("opportunities.scripts.toolbar.shortcuts.select") },
      { keys: ["f"], label: t("opportunities.scripts.toolbar.shortcuts.favorite") },
      { keys: ["e"], label: t("opportunities.scripts.toolbar.shortcuts.aiEdit") },
      { keys: ["/"], label: t("opportunities.scripts.toolbar.shortcuts.search") },
      { keys: ["?"], label: t("opportunities.scripts.toolbar.shortcuts.help") },
    ],
    [t],
  )

  const handleFilterChange = React.useCallback(
    (key: keyof ScriptsFilters, value: string) => setFilters((f) => ({ ...f, [key]: value })),
    [],
  )

  // Selection math is scoped to what's currently loaded — if a user filters
  // such that a selected script disappears from the loaded set, the bulk bar
  // count goes down to match. Matches the old behavior under client-side filtering.
  const allSelected = visibleItems.length > 0 && visibleItems.every((s) => selectedIds.has(s.id))
  const someSelected = visibleItems.some((s) => selectedIds.has(s.id))
  const selectedCount = [...selectedIds].filter((id) => visibleItems.some((s) => s.id === id)).length
  const selectedScripts = visibleItems.filter((s) => selectedIds.has(s.id))

  function toggleSelectAll(checked: boolean) {
    if (checked) {
      setSelectedIds(new Set(visibleItems.map((s) => s.id)))
      // Auto-expand every group so the user can see what was just selected.
      // Without this, "Select all 12" while all groups are collapsed feels like a lie.
      setOpenGroupIds(new Set(groups.map((g) => g.oppId)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const toggleScript = React.useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  function toggleGroupOpen(oppId: number) {
    setOpenGroupIds((prev) => {
      const next = new Set(prev)
      if (next.has(oppId)) next.delete(oppId)
      else next.add(oppId)
      return next
    })
  }

  function toggleGroupSelection(oppId: number, groupScripts: ScriptDto[]) {
    const allInGroup = groupScripts.length > 0 && groupScripts.every((s) => selectedIds.has(s.id))
    if (allInGroup) {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const s of groupScripts) next.delete(s.id)
        return next
      })
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        for (const s of groupScripts) next.add(s.id)
        return next
      })
      // Expand the group so user sees what was selected.
      setOpenGroupIds((prev) => {
        if (prev.has(oppId)) return prev
        const next = new Set(prev)
        next.add(oppId)
        return next
      })
    }
  }

  // ─── Per-row helpers ─────────────────────────────────────────────
  const toggleScriptOpen = React.useCallback((id: string) => {
    setOpenScriptIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Bulk toggle for all scripts in a group — single setState avoids re-rendering
  // 50 rows individually when the user wants to expand a 50-script opportunity.
  const setGroupScriptsOpen = React.useCallback((scriptIds: string[], open: boolean) => {
    setOpenScriptIds((prev) => {
      const next = new Set(prev)
      if (open) for (const id of scriptIds) next.add(id)
      else for (const id of scriptIds) next.delete(id)
      return next
    })
  }, [])

  const toggleFavorite = React.useCallback(
    (script: ScriptDto) => {
      toggleFavoriteMutation.mutate({ scriptId: script.id, isFavorite: !script.isFavorite })
    },
    [toggleFavoriteMutation],
  )

  const registerRowRef = React.useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(id, el)
    else rowRefs.current.delete(id)
  }, [])

  // ─── AI Editor modal ─────────────────────────────────────────────
  const [aiEditorOpen, setAiEditorOpen] = React.useState(false)
  const [aiEditorScriptIds, setAiEditorScriptIds] = React.useState<string[]>([])
  const [processingScriptIds, setProcessingScriptIds] = React.useState<Set<string>>(new Set())

  const aiEditMutation = useAiEditScripts()

  const isScriptProcessing = React.useCallback(
    (scriptId: string) => processingScriptIds.has(scriptId),
    [processingScriptIds],
  )

  // Keep every processing script (and its parent group) expanded at all times.
  // This guards against race conditions where query invalidation or other state
  // updates might inadvertently collapse a script that's mid-edit.
  React.useEffect(() => {
    if (processingScriptIds.size === 0) return
    setOpenScriptIds((prev) => {
      let changed = false
      const next = new Set(prev)
      for (const id of processingScriptIds) {
        if (!next.has(id)) { next.add(id); changed = true }
      }
      return changed ? next : prev
    })
    const parentOppIds = new Set(
      flattenedItems
        .filter((s) => processingScriptIds.has(s.id))
        .map((s) => s.opportunityId),
    )
    if (parentOppIds.size > 0) {
      setOpenGroupIds((prev) => {
        let changed = false
        const next = new Set(prev)
        for (const oppId of parentOppIds) {
          if (!next.has(oppId)) { next.add(oppId); changed = true }
        }
        return changed ? next : prev
      })
    }
  }, [processingScriptIds, flattenedItems])

  const openAiEditor = React.useCallback((ids: string[]) => {
    // Clear any stale pending/error state from a previous edit so a freshly opened
    // modal never inherits a leftover error message or submitting flag. See AD-1648.
    aiEditMutation.reset()
    // Expand all target scripts + their parent groups so they're visible
    setOpenScriptIds((prev) => {
      const next = new Set(prev)
      for (const id of ids) next.add(id)
      return next
    })
    const targetOppIds = new Set(
      flattenedItems.filter((s) => ids.includes(s.id)).map((s) => s.opportunityId),
    )
    setOpenGroupIds((prev) => {
      const next = new Set(prev)
      for (const oppId of targetOppIds) next.add(oppId)
      return next
    })
    setAiEditorScriptIds(ids)
    setAiEditorOpen(true)
    // Depend on the stable `reset` identity, not the whole mutation object — the
    // result object changes identity on every status transition, which would
    // otherwise recreate this callback and rebind the document keydown listener
    // (see effect below) on each poll tick.
  }, [flattenedItems, aiEditMutation.reset])

  const closeAiEditor = React.useCallback(() => {
    setAiEditorOpen(false)
    // Drop the target ids so a later reopen never momentarily renders stale previews.
    setAiEditorScriptIds([])
  }, [])

  // The modal is "submitting" only while the scripts it currently holds are mid-edit.
  // Derived from per-script processing state (not the shared mutation's isPending), so
  // reopening the modal for a different script while a prior edit is still in flight
  // never inherits a stale submitting/locked state. See AD-1648.
  const aiEditorSubmitting = React.useMemo(
    () => aiEditorScriptIds.some((id) => processingScriptIds.has(id)),
    [aiEditorScriptIds, processingScriptIds],
  )

  // Scope the inline error to the scripts this editor session actually holds.
  // `aiEditMutation` is shared across every editor open, so a late failure from
  // a prior edit (script A) would otherwise surface inside a reopen for a
  // different script (script B). Only show the error when the failed run's
  // scriptIds overlap the currently-open editor. See AD-1648.
  const aiEditorError = React.useMemo(() => {
    if (!aiEditMutation.isError) return null
    const failedIds = aiEditMutation.variables?.scriptIds ?? []
    if (!failedIds.some((id) => aiEditorScriptIds.includes(id))) return null
    return aiEditMutation.error?.message ?? t("opportunities.scripts.aiEditor.errorMessage")
  }, [aiEditMutation.isError, aiEditMutation.variables, aiEditMutation.error, aiEditorScriptIds, t])

  async function handleAiEditSubmit(instruction: string) {
    const ids = aiEditorScriptIds
    const tenantId = useAuth.getState().activeTenantId
    if (!tenantId) return
    setProcessingScriptIds((prev) => {
      const next = new Set(prev)
      for (const id of ids) next.add(id)
      return next
    })
    closeAiEditor()
    setSelectedIds(new Set())

    // Each submit owns its loading toast id locally. The mutation is shared across
    // every edit, so a shared ref would be clobbered by a second edit before the
    // first settled, leaking the first toast. mutateAsync + try/finally also avoids
    // the TanStack gotcha where mutate-level callbacks only fire for the latest
    // overlapping call — cleanup here runs per-edit regardless of overlap.
    const toastId = toast.loading(
      ids.length === 1 ? "Editing script…" : `Editing ${ids.length} scripts…`,
    )

    try {
      await aiEditMutation.mutateAsync({ setId, scriptIds: ids, instruction, tenantId })
      trackScriptAiEdited({
        script_ids: ids,
        script_count: ids.length,
        edit_mode: ids.length === 1 ? 'single' : 'bulk',
        edit_prompt_length: instruction.length,
        opportunity_set_id: setId,
      })
    } catch {
      // Error toast + Sentry capture are handled by the mutation's onError.
    } finally {
      toast.dismiss(toastId)
      setProcessingScriptIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) next.delete(id)
        return next
      })
      // Auto-expand edited scripts so the user immediately sees the updated scenes
      setOpenScriptIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) next.add(id)
        return next
      })
      // Auto-expand parent groups for the edited scripts
      const editedOppIds = new Set(
        flattenedItems.filter((s) => ids.includes(s.id)).map((s) => s.opportunityId),
      )
      setOpenGroupIds((prev) => {
        const next = new Set(prev)
        for (const oppId of editedOppIds) next.add(oppId)
        return next
      })
    }
  }

  // ─── Revert dialog ──────────────────────────────────────────────
  const [revertTarget, setRevertTarget] = React.useState<ScriptDto | null>(null)
  const revertMutation = useRevertScript(setId)

  // ─── Send to vendor ──────────────────────────────────────────────
  const [sendToVendorScriptIds, setSendToVendorScriptIds] = React.useState<string[]>([])
  const sendToVendorOpen = sendToVendorScriptIds.length > 0

  const openSendToVendor = React.useCallback((ids: string[]) => {
    const unsentIds = ids.filter((id) => sentScriptsMap[id] == null)
    if (unsentIds.length === 0) {
      toast.message(t("opportunities.scripts.sendDialog.allAlreadySent"))
      return
    }
    if (unsentIds.length < ids.length) {
      toast.message(
        t(
          unsentIds.length === 1
            ? "opportunities.scripts.sendDialog.alreadySentWarning_one"
            : "opportunities.scripts.sendDialog.alreadySentWarning_other",
          { count: ids.length - unsentIds.length },
        ),
      )
    }
    setSendToVendorScriptIds(unsentIds)
  }, [sentScriptsMap, t])

  const getSentMeta = React.useCallback(
    (scriptId: string): SentScriptMeta | null => sentScriptsMap[scriptId] ?? null,
    [sentScriptsMap],
  )

  const navigate = useNavigate()
  const openOrder = React.useCallback(
    (orderId: string) => {
      void navigate({ to: "/marketplace", search: { tab: "orders", order: orderId } })
    },
    [navigate],
  )

  // Resolve the IDs to script previews for the modal. We map from the loaded
  // pages — a script can only be selected once it's been rendered, so this is
  // always populated.
  const aiEditorScripts = React.useMemo(() => {
    const byId = new Map(flattenedItems.map((s) => [s.id, s]))
    return aiEditorScriptIds
      .map((id) => byId.get(id))
      .filter((s): s is ScriptDto => s != null)
      .map((s) => ({
        id: s.id,
        hook: s.hook,
        concept: s.concept,
        opportunityTitle: s.opportunityTitle,
      }))
  }, [aiEditorScriptIds, flattenedItems])

  // ─── Group loaded scripts by opportunityId, preserving server order ──
  // The server returns scripts in opportunity-stable order so a sequential
  // walk yields contiguous groups without sorting. A new group is started
  // whenever the opportunityId changes between adjacent items — this also
  // handles the (rare) case where an opportunity spans a page boundary,
  // because both pages append into the same group.
  const groups = React.useMemo(() => {
    const out: {
      oppId: number
      title: string
      description: string | null
      flowName: string | null
      engine: string | null
      items: ScriptDto[]
    }[] = []
    let current: (typeof out)[number] | null = null
    for (const s of visibleItems) {
      if (current === null || current.oppId !== s.opportunityId) {
        current = {
          oppId: s.opportunityId,
          title: s.opportunityTitle ?? `Opportunity ${s.opportunityId}`,
          description: s.opportunityDescription,
          flowName: s.opportunityFlowName,
          engine: s.opportunityEngine,
          items: [],
        }
        out.push(current)
      }
      current.items.push(s)
    }
    return out
  }, [visibleItems])

  // ─── Keyboard navigation ─────────────────────────────────────────
  // Flat list of every visible script in current display order — used by j/k
  // to navigate linearly across opportunity groups.
  const flatScripts = React.useMemo(
    () => groups.flatMap((g) => g.items.map((s) => ({ script: s, oppId: g.oppId }))),
    [groups],
  )
  const focusedIndex = focusedScriptId
    ? flatScripts.findIndex((f) => f.script.id === focusedScriptId)
    : -1

  const moveFocus = React.useCallback(
    (delta: 1 | -1) => {
      if (flatScripts.length === 0) return
      const currentIdx = focusedScriptId
        ? flatScripts.findIndex((f) => f.script.id === focusedScriptId)
        : -1
      const nextIdx =
        currentIdx < 0
          ? delta > 0
            ? 0
            : flatScripts.length - 1
          : (currentIdx + delta + flatScripts.length) % flatScripts.length
      const target = flatScripts[nextIdx]
      setFocusedScriptId(target.script.id)
      // Auto-expand the destination group so the focused row is on-screen.
      setOpenGroupIds((prev) => {
        if (prev.has(target.oppId)) return prev
        const next = new Set(prev)
        next.add(target.oppId)
        return next
      })
    },
    [flatScripts, focusedScriptId],
  )

  // Scroll the focused row into view (smooth, nearest) whenever it changes.
  React.useEffect(() => {
    if (!focusedScriptId) return
    const el = rowRefs.current.get(focusedScriptId)
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [focusedScriptId])

  // Global keydown handler. Only fires when no input is focused so typing in
  // search/filters keeps working as expected.
  React.useEffect(() => {
    function isEditableTarget() {
      const ae = document.activeElement
      if (!ae) return false
      const tag = ae.tagName
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (ae as HTMLElement).isContentEditable
      )
    }

    function focusedScript(): ScriptDto | null {
      if (focusedIndex < 0) return null
      return flatScripts[focusedIndex]?.script ?? null
    }

    function onKeyDown(e: KeyboardEvent) {
      if (isEditableTarget()) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      switch (e.key) {
        case "j":
        case "ArrowDown": {
          if (e.shiftKey) return
          e.preventDefault()
          moveFocus(1)
          break
        }
        case "k":
        case "ArrowUp": {
          if (e.shiftKey) return
          e.preventDefault()
          moveFocus(-1)
          break
        }
        case " ": {
          const s = focusedScript()
          if (!s) return
          e.preventDefault()
          toggleScript(s.id)
          break
        }
        case "Enter": {
          const s = focusedScript()
          if (!s) return
          e.preventDefault()
          toggleScriptOpen(s.id)
          break
        }
        case "e": {
          const s = focusedScript()
          if (!s) return
          e.preventDefault()
          openAiEditor([s.id])
          break
        }
        case "f": {
          const s = focusedScript()
          if (!s) return
          e.preventDefault()
          void toggleFavorite(s)
          break
        }
        case "?": {
          // `Shift+/` produces "?" on most layouts. No script needs to be focused —
          // the legend is global, like Linear/GitHub/Slack.
          e.preventDefault()
          setShortcutsOpen((prev) => !prev)
          break
        }
      }
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [
    flatScripts,
    focusedIndex,
    moveFocus,
    toggleScript,
    toggleScriptOpen,
    toggleFavorite,
    openAiEditor,
  ])

  // ─── Infinite scroll ────────────────────────────────────────────
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)
  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const infiniteScrollRecheckKey = `${flattenedItems.length}:${groups.length}:${openGroupIds.size}:${openScriptIds.size}`
  useInfiniteScroll(
    sentinelRef,
    scrollContainerRef,
    () => void listQuery.fetchNextPage(),
    listQuery.hasNextPage ?? false,
    listQuery.isFetchingNextPage,
    {
      enabled: !listQuery.isLoading && !listQuery.isError,
      recheckKey: infiniteScrollRecheckKey,
    },
  )

  // ─── R9: full-tab loading skeleton (initial fetch only) ─────────
  if (listQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4" data-testid="scripts-loading">
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-48 rounded-md" />
          <Skeleton className="h-8 w-24 rounded-md" />
          <Skeleton className="h-8 w-24 rounded-md" />
          <Skeleton className="h-8 w-24 rounded-md" />
          <div className="ml-auto">
            <Skeleton className="h-8 w-28 rounded-md" />
          </div>
        </div>
        <Skeleton className="h-14 w-full rounded-md" />
        <div className="flex items-center gap-3">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-3.5 w-36 rounded" />
        </div>
        <ScriptGroupSkeleton scriptsCount={3} />
        <ScriptGroupSkeleton />
        <ScriptGroupSkeleton />
      </div>
    )
  }

  // ─── R9: full-tab error (first page failed; subsequent-page errors
  // surface inline at the end of the list instead) ────────────────
  if (listQuery.isError && flattenedItems.length === 0) {
    return (
      <div
        className="flex min-h-[200px] flex-col items-center justify-center gap-3"
        data-testid="scripts-error"
      >
        <p className="text-sm text-muted-foreground">{t("opportunities.scripts.error")}</p>
        <button
          type="button"
          onClick={() => void listQuery.refetch()}
          className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Icon name="refresh-cw" size="xs" />
          {t("common.retry")}
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* Fixed top: toolbar + diversification + select-all */}
      <div className="shrink-0 flex flex-col gap-4 pb-4">
      {/* Section 1 — Toolbar */}
      <ScriptsToolbar
        search={shouldRevealTaskTarget ? "" : inputSearch}
        onSearchChange={handleSearchChange}
        filters={listFilters}
        onFilterChange={handleFilterChange}
        onClearAll={() => {
          cancelDebounce()
          setInputSearch("")
          setDebouncedSearch("")
          // Preserve the view-mode toggle (favoritesOnly) — it's a top-level lens,
          // not one of the dropdown filters the "Clear filters" button represents.
          setFilters((f) => ({ ...DEFAULT_FILTERS, favoritesOnly: f.favoritesOnly }))
        }}
        topicOptions={topicOptions}
        conceptOptions={conceptOptions}
        presenterOptions={presenterOptions}
        backgroundOptions={backgroundOptions}
        sequenceOptions={sequenceOptions}
      >
      </ScriptsToolbar>

      {/* Section 2 — Diversification impact.
          Under server-side pagination we no longer hold the full unfiltered
          baseline locally — the chart now compares the loaded view against
          itself, which renders a single static mix instead of the today-vs-
          after delta. A follow-up could surface an unfiltered topic-mix
          baseline from a dedicated endpoint to restore the comparison. */}
      {/* <DiversificationImpact scripts={flattenedItems} filtered={flattenedItems} /> */}

      {/* Select all + Suggest Script + quiet shortcut hint. This row always renders
          (even with zero scripts) so users can create the first script; the
          "Select all" affordance stays gated behind visibleItems.length as before. */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {visibleItems.length > 0 && (
            <>
              <Checkbox
                id="select-all-scripts"
                checked={someSelected && !allSelected ? "indeterminate" : allSelected}
                onCheckedChange={(v) => toggleSelectAll(v === true)}
                className="shrink-0"
              />
              <label htmlFor="select-all-scripts" className="cursor-pointer text-xs text-muted-foreground">
                {t("opportunities.scripts.diversification.selectAll", { count: visibleItems.length })}
              </label>
              {!isClientFiltered && totalCount > flattenedItems.length && (
                <span className="text-[11px] tabular-nums text-muted-foreground/70">
                  {t("opportunities.scripts.loadedOfTotal", {
                    loaded: flattenedItems.length,
                    total: totalCount,
                    defaultValue: "{{loaded}} of {{total}}",
                  })}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ai"
            size="sm"
            iconStart="sparkles"
            onClick={() => setSuggestOpen(true)}
            className="border border-[var(--ai)]/40"
          >
            {t("opportunities.scripts.suggest.button")}
          </Button>
          {groups.length > 0 && (
            <button
              type="button"
              onClick={() => setShortcutsOpen(true)}
              className="flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[11px] text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t("opportunities.scripts.toolbar.shortcuts.title")}
            >
              <span>{t("opportunities.scripts.toolbar.shortcuts.footerHint")}</span>
              <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                ?
              </kbd>
            </button>
          )}
        </div>
      </div>

      </div>{/* end fixed top */}

      {/* Section 3 — Opportunity groups (scrollable, fills remaining height) */}
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-visible pb-4 [scrollbar-gutter:stable]"
      >
      {groups.length === 0 ? (
        <div
          className="flex min-h-[120px] flex-col items-center justify-center gap-3 rounded-md border border-border bg-card"
          data-testid={filtersActive ? "scripts-empty-filtered" : "scripts-empty"}
        >
          <p className="text-sm text-muted-foreground">
            {filtersActive ? t("opportunities.scripts.emptyFiltered") : t("opportunities.scripts.empty")}
          </p>
          {filtersActive && (
            <button
              type="button"
              onClick={() => {
                cancelDebounce()
                setInputSearch("")
                setDebouncedSearch("")
                // Preserve the view-mode toggle (favoritesOnly) — matches the toolbar's
                // Clear filters behaviour. If favorites-only is what produced the empty
                // result, the user can switch lenses; we don't silently flip it for them.
                setFilters((f) => ({ ...DEFAULT_FILTERS, favoritesOnly: f.favoritesOnly }))
              }}
              className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {t("common.clearFilters")}
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <OpportunityScriptGroup
              key={group.oppId}
              title={group.title}
              description={group.description}
              flowName={group.flowName}
              engine={group.engine}
              scripts={group.items}
              selectedIds={selectedIds}
              openScriptIds={openScriptIds}
              focusedScriptId={focusedScriptId}
              isOpen={openGroupIds.has(group.oppId)}
              onToggleOpen={() => toggleGroupOpen(group.oppId)}
              onToggleSelected={toggleScript}
              onToggleGroupSelection={() => toggleGroupSelection(group.oppId, group.items)}
              onToggleScriptOpen={toggleScriptOpen}
              onSetAllScriptsOpen={setGroupScriptsOpen}
              onToggleScriptFavorite={toggleFavorite}
              onRequestScriptFocus={setFocusedScriptId}
              onScriptAiEdit={(id) => openAiEditor([id])}
              onScriptRevert={(script) => setRevertTarget(script)}
              onScriptSceneReorder={handleSceneReorder}
              onScriptSendToVendor={(id) => openSendToVendor([id])}
              onScriptOpenOrder={openOrder}
              isScriptProcessing={isScriptProcessing}
              getSentMeta={getSentMeta}
              registerRowRef={registerRowRef}
              search={debouncedSearch}
              setId={setId}
            />
          ))}

          {/* R9: pagination sentinel — covers loading-more, next-page-error, and end-of-list */}
          <div ref={sentinelRef} data-testid="scripts-pagination-sentinel" className="pt-1">
            {listQuery.isFetchingNextPage && (
              <div className="flex flex-col gap-3" data-testid="scripts-loading-more">
                <ScriptGroupSkeleton scriptsCount={2} />
                <p className="text-center text-xs text-muted-foreground/70">
                  {t("opportunities.scripts.loadingMore", { defaultValue: "Loading more…" })}
                </p>
              </div>
            )}
            {listQuery.isError && flattenedItems.length > 0 && !listQuery.isFetchingNextPage && (
              <div
                className="flex flex-col items-center justify-center gap-2 rounded-lg border border-border/50 bg-card px-4 py-4"
                data-testid="scripts-next-page-error"
              >
                <p className="text-xs text-muted-foreground">
                  {t("opportunities.scripts.nextPageError", {
                    defaultValue: "Couldn't load more scripts.",
                  })}
                </p>
                <button
                  type="button"
                  onClick={() => void listQuery.fetchNextPage()}
                  className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Icon name="refresh-cw" size="xs" />
                  {t("common.retry")}
                </button>
              </div>
            )}
            {!isGenerating && !listQuery.hasNextPage && !listQuery.isFetchingNextPage && totalCount > 0 && flattenedItems.length >= PAGE_SIZE && (
              <p className="pt-2 text-center text-[11px] text-muted-foreground/60">
                {t("opportunities.scripts.endOfList", {
                  total: totalCount,
                  defaultValue: "All {{total}} scripts loaded",
                })}
              </p>
            )}
          </div>
        </div>
      )}

      </div>{/* end scrollable groups */}

      {/* AI Script Editor modal */}
      <AiScriptEditorModal
        open={aiEditorOpen}
        onClose={closeAiEditor}
        scripts={aiEditorScripts}
        onSubmit={handleAiEditSubmit}
        isSubmitting={aiEditorSubmitting}
        error={aiEditorError}
      />

      {/* Revert confirmation dialog */}
      <RevertScriptDialog
        open={revertTarget !== null}
        onOpenChange={(open) => { if (!open) setRevertTarget(null) }}
        scriptTitle={
          revertTarget
            ? getScriptDisplayTitle(
                revertTarget,
                t("opportunities.scripts.untitledTitle", "Untitled script"),
              )
            : null
        }
        isReverting={revertMutation.isPending}
        onConfirm={() => {
          if (!revertTarget) return
          revertMutation.mutate(
            { setId, scriptId: revertTarget.id },
            { onSettled: () => setRevertTarget(null) },
          )
        }}
      />

      {/* Floating bulk action bar */}
      <ScriptsBulkActionBar
        setId={setId}
        selectedCount={selectedCount}
        selectedScripts={selectedScripts}
        sentScriptsMap={sentScriptsMap}
        onClearSelection={() => setSelectedIds(new Set())}
        onAiEditSelected={(ids) => openAiEditor(ids)}
        onSendToVendor={(ids) => openSendToVendor(ids)}
      />

      {/* Keyboard shortcut legend — opened by `?`, closed by Esc or the X. */}
      <KeyboardShortcutsDialog
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
        title={t("opportunities.scripts.toolbar.shortcuts.title")}
        shortcuts={scriptShortcuts}
      />

      {/* Send to vendor dialog */}
      {activeTenantId && (
        <SendToVendorDialog
          open={sendToVendorOpen}
          onOpenChange={(v) => { if (!v) setSendToVendorScriptIds([]) }}
          scriptIds={sendToVendorScriptIds}
          sentScriptsMap={sentScriptsMap}
          tenantId={activeTenantId}
        />
      )}

      {/* Suggest Script modal — only mounted once a tenant is resolved, so the
          resolve-links pre-step never fires with an empty tenantId. */}
      {activeTenantId && (
        <SuggestScriptModal
          open={suggestOpen}
          onClose={() => setSuggestOpen(false)}
          isSubmitting={suggestJob.isSubmitting}
          setId={setId}
          tenantId={activeTenantId}
          onSubmit={(description, variations, referenceUrls) => {
            suggestJob.execute({ description, variations, referenceUrls })
            setSuggestOpen(false)
          }}
        />
      )}

    </div>
  )
}
